import Layout from "../Layout";

const TABLE_OF_CONTENTS = [
  { id: "who-we-are", label: "Who we are" },
  { id: "information-we-collect", label: "Information we collect" },
  { id: "how-we-use-information", label: "How we use your information" },
  { id: "legal-bases", label: "Legal bases" },
  { id: "how-we-share", label: "How we share information" },
  { id: "international-transfers", label: "International data transfers" },
  { id: "data-retention", label: "Data retention" },
  { id: "security", label: "Security" },
  { id: "your-rights", label: "Your choices and rights" },
  { id: "childrens-privacy", label: "Children's privacy" },
  { id: "changes", label: "Changes to this policy" },
  { id: "contact", label: "Contact us" },
] as const;

function Hero(): React.ReactElement {
  return (
    <section className="px-4 pt-40 pb-10">
      <div className="max-w-3xl mx-auto text-center">
        <h1 className="mb-4 text-5xl font-bold leading-tight tracking-tight md:text-6xl">
          Privacy Policy
        </h1>
        <p className="text-surface-foreground/60">
          Last updated: 28 November 2025
        </p>
      </div>
    </section>
  );
}

function TableOfContents(): React.ReactElement {
  return (
    <nav aria-label="Table of contents" className="px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <p className="text-xs font-medium text-surface-foreground/30 uppercase tracking-wider mb-3">
          Contents
        </p>
        <ol className="space-y-1 text-[15px]">
          {TABLE_OF_CONTENTS.map(({ id, label }, index) => (
            <li key={id}>
              <a
                href={`#${id}`}
                className="text-surface-foreground/80 hover:underline"
              >
                <span className="text-surface-foreground/50 tabular-nums mr-2">
                  {index + 1}.
                </span>
                {label}
              </a>
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function Intro(): React.ReactElement {
  return (
    <section className="px-4 pt-4 pb-8">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <p>
          This Privacy Policy explains how Infralyst ("we", "us", "our") collects, uses, and
          protects information when you use our website and products.
        </p>
        <p>
          We've tried to write this in clear, straightforward language. This document is for
          information only and is not legal advice. If you have questions, you can always email us
          at <a href="mailto:hello@infralyst.io">hello@infralyst.io</a>.
        </p>
      </div>
    </section>
  );
}

function WhoWeAre(): React.ReactElement {
  return (
    <section id="who-we-are" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-ul:my-4 prose-ul:pl-6 prose-li:my-1 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">1.</span>
          Who we are
        </h2>
        <p>Infralyst is operated by:</p>
        <address className="not-italic mb-4 text-[15px] leading-7 pl-4 border-l-2 border-surface-foreground/10">
          <strong>Saankofi Limited (Hong Kong)</strong>
          <br />
          14/F Hill Lodge
          <br />
          1 Lok Fung Path
          <br />
          Fo Tan, New Territories
          <br />
          Hong Kong
          <br />
          Company number: 79143950
        </address>
        <p>
          Infralyst helps engineering teams reduce cloud spend by turning under-utilized
          infrastructure into small, reviewable Terraform pull requests.
        </p>
        <p>This Privacy Policy applies to:</p>
        <ul>
          <li>The Infralyst web application at infralyst.io</li>
          <li>The marketing site at infralyst.io</li>
          <li>Our articles and documentation</li>
          <li>Any other services that link to this policy</li>
        </ul>
      </div>
    </section>
  );
}

function InformationWeCollect(): React.ReactElement {
  return (
    <section id="information-we-collect" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-h3:text-lg prose-h3:font-semibold prose-h3:mb-3 prose-h3:mt-8 prose-h4:text-base prose-h4:font-semibold prose-h4:mb-2 prose-h4:mt-6 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-ul:my-4 prose-ul:pl-6 prose-ol:my-4 prose-ol:pl-6 prose-li:my-1 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">2.</span>
          Information we collect
        </h2>
        <p>We collect information in three main ways:</p>
        <ol>
          <li>Information you give us directly</li>
          <li>Data we receive from services you connect to Infralyst (like AWS and GitHub)</li>
          <li>Usage and analytics data about how you use Infralyst</li>
        </ol>
        <p>We describe each in more detail below.</p>

        <h3>2.1 Information you provide to us</h3>
        <p>When you create and use an Infralyst account, we collect:</p>
        <ul>
          <li>
            <strong>Account details</strong>
            <ul>
              <li>Name (where provided)</li>
              <li>Email address</li>
              <li>
                Authentication details (e.g. magic link tokens, password hashes if you choose a
                password)
              </li>
            </ul>
          </li>
          <li>
            <strong>Workspace / organization details</strong>
            <ul>
              <li>Workspace / organization name</li>
              <li>Workspace slug / external ID</li>
              <li>Team membership and roles (e.g. owner, admin, member)</li>
            </ul>
          </li>
          <li>
            <strong>Billing and subscription information</strong>
            <ul>
              <li>Plan type (e.g. Free, Pro)</li>
              <li>Subscription status and Stripe customer/subscription IDs</li>
              <li>
                Optional tax information you provide (company name, billing address, tax ID
                type/value, verification status)
              </li>
            </ul>
          </li>
        </ul>
        <p>Payment card details themselves are handled by Stripe and never touch our servers.</p>
        <ul>
          <li>
            <strong>Support and communication</strong>
            <ul>
              <li>Messages you send us by email or through the app</li>
              <li>Your contact details and any information you choose to include in those messages</li>
            </ul>
          </li>
        </ul>

        <h3>2.2 Data from your connected services</h3>
        <p>
          Infralyst is built to be read-only by design, with a narrow set of permissions. We use
          the minimum data needed to find cost-saving opportunities and generate safe, reviewable
          Terraform PRs.
        </p>

        <h4>AWS</h4>
        <p>
          When you connect AWS, you add the Infralyst Terraform module and configure a read-only
          IAM role for us to assume. We store:
        </p>
        <ul>
          <li>AWS account IDs and a human-readable display name</li>
          <li>The role ARN and connection status</li>
          <li>
            Regions, services, and resource identifiers that are relevant to our analysis (for
            example, EC2 instance IDs, Auto Scaling Group names, cluster names)
          </li>
          <li>
            Utilization metrics and configuration metadata for those resources (for example,
            CPU/memory usage, instance type, desired/min/max capacity, warm pool size)
          </li>
        </ul>
        <p>We do not store your AWS access keys or secrets. Access is via role assumption.</p>

        <h4>GitHub & Terraform state</h4>
        <p>When you connect GitHub, we store:</p>
        <ul>
          <li>
            GitHub organization, repo owner, and repo name for infrastructure repositories you
            select
          </li>
          <li>Branch names we use to open PRs</li>
          <li>Webhook IDs/secrets so we can receive relevant events</li>
          <li>
            Terraform state mapping information, such as:
            <ul>
              <li>Which AWS accounts and projects a state file relates to</li>
              <li>S3 bucket names, keys, and regions (for S3-backed state)</li>
              <li>Filenames and paths for state files inside repos</li>
            </ul>
          </li>
        </ul>
        <p>
          We do not ingest your application code or Terraform module contents for any purpose other
          than cost-saving analysis and PR generation.
        </p>
        <p>When a recommendation is implemented, we store:</p>
        <ul>
          <li>Linked recommendation IDs</li>
          <li>The pull request URL</li>
          <li>Status (pending, PR created, merged, closed)</li>
        </ul>

        <h4>Slack (optional)</h4>
        <p>If you connect Slack for notifications and PR generation:</p>
        <ul>
          <li>
            We store workspace/channel identifiers, destinations, and metadata needed to send
            notifications or trigger PRs.
          </li>
          <li>We log which notifications were sent and when.</li>
          <li>We do not index or store the full contents of your wider Slack workspace.</li>
        </ul>
        <p>You can disconnect Slack at any time from your workspace settings.</p>

        <h3>2.3 Usage, analytics, and logs</h3>
        <p>To understand how Infralyst is used and keep the service reliable, we collect:</p>
        <ul>
          <li>
            <strong>Session and device data</strong>
            <ul>
              <li>IP address</li>
              <li>Browser user agent</li>
              <li>Login timestamps</li>
              <li>Basic device and browser information</li>
            </ul>
          </li>
          <li>
            <strong>Product usage data</strong>
            <ul>
              <li>Which pages/screens you visit</li>
              <li>
                Which features you use (for example, connecting an AWS account, generating a PR,
                dismissing a recommendation)
              </li>
              <li>Clicks, events, and timestamps</li>
            </ul>
          </li>
        </ul>
        <p>
          We use PostHog for product analytics and Sentry for error monitoring. We configure these
          tools to use IDs and metadata and avoid sending the contents of your infrastructure
          resources or other unnecessary data.
        </p>

        <h3>2.4 Cookies and similar technologies</h3>
        <p>We use cookies and similar technologies to:</p>
        <ul>
          <li>Keep you signed in</li>
          <li>Provide core app functionality</li>
          <li>Measure simple product usage (via PostHog)</li>
          <li>Improve reliability and performance</li>
        </ul>
        <p>
          You can control cookies through your browser settings. If you block all cookies, some
          parts of Infralyst may not work correctly.
        </p>
      </div>
    </section>
  );
}

function HowWeUseInformation(): React.ReactElement {
  return (
    <section id="how-we-use-information" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-ul:my-4 prose-ul:pl-6 prose-li:my-1 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">3.</span>
          How we use your information
        </h2>
        <p>We use the information described above to:</p>
        <ul>
          <li>
            <strong>Provide and operate Infralyst</strong>
            <ul>
              <li>Create and manage your account and workspaces</li>
              <li>Connect to your AWS accounts, GitHub repos, and optional Slack workspace</li>
              <li>
                Analyze infrastructure usage and configuration to detect cost-saving opportunities
              </li>
              <li>Generate and track Terraform pull requests</li>
            </ul>
          </li>
          <li>
            <strong>Improve and develop the product</strong>
            <ul>
              <li>Understand which features are useful</li>
              <li>Diagnose issues and errors</li>
              <li>Plan improvements based on real usage patterns</li>
            </ul>
          </li>
          <li>
            <strong>Communicate with you</strong>
            <ul>
              <li>
                Send service-related emails (for example, onboarding, security notices, changes to
                policies)
              </li>
              <li>Respond to your support requests</li>
              <li>
                Share information about new features or updates (you can usually opt out of
                non-essential emails)
              </li>
            </ul>
          </li>
          <li>
            <strong>Billing and account management</strong>
            <ul>
              <li>Manage subscriptions, invoicing, and payments through Stripe</li>
              <li>Handle upgrades, downgrades, and cancellations</li>
            </ul>
          </li>
          <li>
            <strong>Security, fraud prevention, and legal compliance</strong>
            <ul>
              <li>Monitor for suspicious or abusive activity</li>
              <li>Investigate and respond to security issues</li>
              <li>Comply with applicable laws and regulations</li>
            </ul>
          </li>
        </ul>
        <p>We do not sell your data or use it for third-party advertising.</p>
      </div>
    </section>
  );
}

function LegalBases(): React.ReactElement {
  return (
    <section id="legal-bases" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-ul:my-4 prose-ul:pl-6 prose-li:my-1 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">4.</span>
          Legal bases (for UK/EU visitors)
        </h2>
        <p>
          While Infralyst is operated from Hong Kong, if you are in the UK or EEA we generally
          process your personal data on the following bases:
        </p>
        <ul>
          <li>
            <strong>Contract</strong>: to provide you with the services you signed up for.
          </li>
          <li>
            <strong>Legitimate interests</strong>: to improve and secure our services, understand
            usage, and prevent abuse, where these interests are not overridden by your rights.
          </li>
          <li>
            <strong>Legal obligations</strong>: where we must keep certain records or respond to
            lawful requests.
          </li>
        </ul>
      </div>
    </section>
  );
}

function HowWeShareInformation(): React.ReactElement {
  return (
    <section id="how-we-share" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-h3:text-lg prose-h3:font-semibold prose-h3:mb-3 prose-h3:mt-8 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-ul:my-4 prose-ul:pl-6 prose-li:my-1 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">5.</span>
          How we share information
        </h2>
        <p>
          We share information only where necessary to provide the service, comply with the law, or
          protect our rights. In particular:
        </p>

        <h3>5.1 Service providers ("processors")</h3>
        <p>
          We use third-party providers to run Infralyst. Depending on how you use the service, your
          data may be processed by:
        </p>
        <ul>
          <li>Hosting and infrastructure providers (for example, cloud hosting, databases, backups)</li>
          <li>Analytics and error monitoring: PostHog and Sentry</li>
          <li>Payment processing: Stripe</li>
          <li>Email delivery: AWS SES or similar providers</li>
        </ul>
        <p>
          These providers act on our instructions and are only allowed to use your data to provide
          their services to us.
        </p>

        <h3>5.2 Business transfers</h3>
        <p>
          If we are involved in a merger, acquisition, financing, or sale of all or part of our
          business, your information may be transferred as part of that transaction, subject to
          appropriate confidentiality protections.
        </p>

        <h3>5.3 Legal and safety</h3>
        <p>We may disclose information if we reasonably believe it is necessary to:</p>
        <ul>
          <li>Comply with applicable law, regulation, or legal process</li>
          <li>Respond to lawful requests from public authorities</li>
          <li>Protect the rights, property, or safety of Infralyst, our users, or others</li>
        </ul>
        <p>We do not share your data with third parties for their own marketing purposes.</p>
      </div>
    </section>
  );
}

function InternationalDataTransfers(): React.ReactElement {
  return (
    <section id="international-transfers" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">6.</span>
          International data transfers
        </h2>
        <p>
          Infralyst is operated from Hong Kong, and some of our service providers may be located in
          other countries.
        </p>
        <p>
          By using Infralyst, you understand that your information may be transferred to, stored
          in, and processed in countries that may have different data protection laws than your
          own. Where required, we take steps to ensure that appropriate safeguards are in place.
        </p>
      </div>
    </section>
  );
}

function DataRetention(): React.ReactElement {
  return (
    <section id="data-retention" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-ul:my-4 prose-ul:pl-6 prose-li:my-1 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">7.</span>
          Data retention
        </h2>
        <p>
          We keep information only for as long as we need it for the purposes described in this
          policy, unless a longer retention period is required or permitted by law.
        </p>
        <p>In general:</p>
        <ul>
          <li>
            <strong>Account and workspace data</strong>
            <ul>
              <li>Kept for as long as your workspace is active.</li>
              <li>
                If you delete your workspace, we delete or anonymize associated data within a
                reasonable period, subject to backup and log retention.
              </li>
            </ul>
          </li>
          <li>
            <strong>Infrastructure metrics and analyses</strong>
            <ul>
              <li>
                Detailed infrastructure metrics are typically kept for up to 12 months to support
                trends and safe rightsizing analysis, unless you delete your workspace sooner.
              </li>
            </ul>
          </li>
          <li>
            <strong>Logs and security-related data</strong>
            <ul>
              <li>
                Kept for a limited time to ensure security, troubleshoot issues, and maintain
                service reliability.
              </li>
            </ul>
          </li>
          <li>
            <strong>Billing and transaction records</strong>
            <ul>
              <li>Kept as long as necessary for accounting, tax, and legal obligations.</li>
            </ul>
          </li>
        </ul>
        <p>
          If you'd like more detail on how long we keep a specific type of data, you can contact us
          at <a href="mailto:hello@infralyst.io">hello@infralyst.io</a>.
        </p>
      </div>
    </section>
  );
}

function Security(): React.ReactElement {
  return (
    <section id="security" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-ul:my-4 prose-ul:pl-6 prose-li:my-1 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">8.</span>
          Security
        </h2>
        <p>We take security seriously and design Infralyst to minimize access and permissions:</p>
        <ul>
          <li>
            <strong>Read-only by design</strong>: access to your AWS accounts is via narrowly
            scoped, read-only IAM roles. Apart from creating branches and PRs, everything we do is
            read-only.
          </li>
          <li>
            <strong>Least privilege</strong>: permissions for AWS, GitHub, and Slack are limited
            to what we need to provide the service.
          </li>
          <li>
            <strong>Encryption</strong>: we use HTTPS to encrypt traffic in transit and encrypt
            sensitive data at rest.
          </li>
          <li>
            <strong>Infrastructure</strong>: we run on managed infrastructure with up-to-date
            operating systems, libraries, and dependencies.
          </li>
          <li>
            <strong>Access controls</strong>: access to production systems is limited to
            authorized personnel and protected by authentication controls.
          </li>
        </ul>
        <p>
          No method of transmission or storage is perfectly secure. However, we work to protect
          your information and improve our security practices over time.
        </p>
        <p>
          If you believe you've found a security vulnerability, please email our security team at{" "}
          <a href="mailto:security@infralyst.io">security@infralyst.io</a>.
        </p>
      </div>
    </section>
  );
}

function YourChoicesAndRights(): React.ReactElement {
  return (
    <section id="your-rights" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-ul:my-4 prose-ul:pl-6 prose-li:my-1 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">9.</span>
          Your choices and rights
        </h2>
        <p>
          Depending on where you live, you may have certain rights over your personal data, such
          as:
        </p>
        <ul>
          <li>Accessing the personal data we hold about you</li>
          <li>Requesting correction of inaccurate information</li>
          <li>Requesting deletion of your personal data</li>
          <li>Objecting to or restricting certain types of processing</li>
          <li>Porting your data to another service, where technically feasible</li>
        </ul>
        <p>You can also:</p>
        <ul>
          <li>
            <strong>Manage your account</strong>: update your details from within the app.
          </li>
          <li>
            <strong>Delete your workspace</strong>: remove a workspace and associated data,
            subject to retention described above.
          </li>
          <li>
            <strong>Unsubscribe from emails</strong>: use the unsubscribe links in non-essential
            emails or contact us.
          </li>
        </ul>
        <p>
          To exercise any of these rights, contact us at{" "}
          <a href="mailto:hello@infralyst.io">hello@infralyst.io</a>. We may need to verify your
          identity before responding. We will respond within a reasonable time and in accordance
          with applicable laws.
        </p>
      </div>
    </section>
  );
}

function ChildrensPrivacy(): React.ReactElement {
  return (
    <section id="childrens-privacy" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">10.</span>
          Children's privacy
        </h2>
        <p>
          Infralyst is designed for professional use by adults. We do not knowingly collect
          personal data from children under 16. If you believe a child has provided us with
          personal data, please contact us so we can delete it.
        </p>
      </div>
    </section>
  );
}

function ChangesToPolicy(): React.ReactElement {
  return (
    <section id="changes" className="px-4 pt-10 pb-6 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-ul:my-4 prose-ul:pl-6 prose-li:my-1 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">11.</span>
          Changes to this policy
        </h2>
        <p>
          We may update this Privacy Policy from time to time. When we make material changes, we
          will:
        </p>
        <ul>
          <li>Update the "Last updated" date at the top</li>
          <li>Where appropriate, notify you by email or through the app</li>
        </ul>
        <p>Your continued use of Infralyst after a change means you accept the updated policy.</p>
      </div>
    </section>
  );
}

function ContactUs(): React.ReactElement {
  return (
    <section id="contact" className="px-4 pt-10 pb-16 scroll-mt-24">
      <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-5 prose-h2:mt-0 prose-p:leading-7 prose-p:text-[15px] prose-p:mb-4 prose-ul:my-4 prose-ul:pl-6 prose-li:my-1 prose-a:text-inherit prose-a:underline prose-a:decoration-surface-foreground/30 hover:prose-a:decoration-primary prose-a:underline-offset-2">
        <h2>
          <span className="text-surface-foreground/40 font-normal mr-2">12.</span>
          Contact us
        </h2>
        <p>
          If you have questions about this Privacy Policy or how we handle your data, please
          contact:
        </p>
        <ul>
          <li>
            <strong>Email (general privacy & data questions):</strong>{" "}
            <a href="mailto:hello@infralyst.io">hello@infralyst.io</a>
          </li>
          <li>
            <strong>Email (security & vulnerabilities):</strong>{" "}
            <a href="mailto:security@infralyst.io">security@infralyst.io</a>
          </li>
          <li>
            <strong>Address:</strong> Saankofi Limited (Hong Kong), 14/F Hill Lodge, 1 Lok Fung
            Path, Fo Tan, New Territories, Hong Kong
          </li>
        </ul>
        <p>We're happy to clarify how we handle data or discuss any concerns you may have.</p>
      </div>
    </section>
  );
}

export default function Show(): React.ReactElement {
  return (
    <Layout
      title="Privacy Policy"
      description="Learn how Infralyst collects, uses, and protects your information when you use our website and products."
      showGradient={false}
    >
      <Hero />
      <TableOfContents />
      <Intro />
      <WhoWeAre />
      <InformationWeCollect />
      <HowWeUseInformation />
      <LegalBases />
      <HowWeShareInformation />
      <InternationalDataTransfers />
      <DataRetention />
      <Security />
      <YourChoicesAndRights />
      <ChildrensPrivacy />
      <ChangesToPolicy />
      <ContactUs />
    </Layout>
  );
}
